/*
* RichTextWrapper.js
* Created on 29-1-2019
* @author reinier@ranj.nl and swen@ranj.nl for &ranj
*
* TODO: document usage and caveats
*
* Tested with createjs versions:
* - 0.8.2
* - 1.0.0
*
* Changelog:
* - 201908: improved how code is tied in with createjs drawing pipeline
* - 2019Q1: first implementation
* - 2021Q4: port to es6
*/

import RichTextRenderer from "./RichTextRenderer";
import RichTextStyling from "./RichTextStyling";

export default class RichTextWrapper {
    constructor(textObject, styling, container) {
        this._container = container || new createjs.Container().set({
            x: textObject.x,
            y: textObject.y
        });
        this._styling = styling || RichTextStyling.fromFontPropertiesOnTextObject(textObject);

        //Copy relevant properties from original Text
        this.font = textObject.font;
        this.textBaseline = textObject.textBaseline;
        this.textAlign = textObject.textAlign;
        this.lineHeight = textObject.lineHeight;
        this.lineWidth = textObject.lineWidth;
        this.color = textObject.color;

        //Replace original Text with our container
        if (textObject.parent) {
            var idx = textObject.parent.getChildIndex(textObject);
            textObject.parent.addChildAt(this._container, idx);
            textObject.parent.removeChild(textObject);
        } else {
            console.warn("[RichTextWrapper] Cannot add container to the parent of the text object, because the textobject doesn't have a parent.");
        }
    }

    get text() {
        return this._text;
    }

    set text(value) {
        this._text = value;
        this._calcLines(this._text);
        this._render();
    }

    getContainer() {
        return this._container;
    }

    _render() {
        if (this.richTextRenderer) {
            this.richTextRenderer.destroy();
        }
        this.richTextRenderer = new RichTextRenderer(this._styling, this._container);
        this.richTextRenderer.renderLines(this.linesData, this.textAlign, this.lineHeight, this.color);
    };

    _calcLines(text) {
        //Split text in hardlines, and hardlines in words, taking tags into account
        //Use keys in _styling to determine valid tags
        //For now, we dont support nested tags. Specify combined tags in _styling and use those in markedup string, e.g. _styling.bi="bold italic 10px Arial" ??
        var tokens = text.split(REGEX_TOKENS);
        var wordsPerTag = {};
        var hardLines = [];
        var line = [];
        for (var i = 0; i < tokens.length; i++) {
            var token = tokens[i];
            var res = REGEX_TAGS.exec(token);
            //if not between tags, there is no match, and nothing captured -> use original text:
            var font = this.font;
            var text;
            var fullGroup;
            var tag = "tagless";
            if (res != null) {
                fullGroup = res[0];
                tag = res[1].split(' ')[0]; // contains attributes, strip the attributes
                text = res[2];
                if (this._styling[tag]) font = this._styling[tag].font;
            }
            else {
                text = token;
                fullGroup = token;
            }
            var words = text.split(/(\s)/);//this also finds newlines as words!
            var n = words.length;
            for (var j = 0; j < n; j++) {
                var word = words[j];
                if (word == "") {
                    continue;
                }
                else if (word.match(REGEX_LINEBREAKS) == null) {
                    var wordObj = { text: word, font: font, width: null, tag: tag, full: fullGroup };//TODO: use a proper 'class' object for this?
                    line.push(wordObj);
                    //also collect per font so we can do word measuring more performant (less context switching)
                    if (wordsPerTag[tag] == null) wordsPerTag[tag] = [];
                    wordsPerTag[tag].push(wordObj);
                }
                else {
                    hardLines.push(line);
                    line = [];
                }
            }
        }
        hardLines.push(line);

        // Now measure words using wordsPerTag, and store measurements on wordObjs
        // Prepare working context
        var ctx = createjs.Text._workingContext;
        ctx.save();
        ctx.font = this.font || "10px sans-serif";
        ctx.textAlign = "left";// textAlign always left, because this will be used for rendering single words.
        ctx.textBaseline = this.textBaseline || "top";
        ctx.lineJoin = "miter";
        ctx.miterLimit = 2.5;
        var fontOrig = ctx.font;
        for (var tag in wordsPerTag) {
            var wordObjs = wordsPerTag[tag];
            for (var i = 0; i < wordObjs.length; i++) {
                wordObj = wordObjs[i];
                ctx.font = wordObj.font;
                var word = wordObj.text;
                wordObj.width = ctx.measureText(word).width;
            }
        }
        ctx.font = fontOrig;
        ctx.restore();

        // Now apply wordwrapping where necessary:
        var lines = [];
        var maxW = 0;
        for (var i = 0; i < hardLines.length; i++) {
            var wordsDataForHardLine = hardLines[i];

            if (this.lineWidth != null) {
                // start filling line
                var w = 0;
                var wordsDataForLine = [];
                var lineData = { wordsData: wordsDataForLine, width: 0 };
                lines.push(lineData);

                for (var j = 0; j < wordsDataForHardLine.length; j++) {
                    var wordData = wordsDataForHardLine[j];
                    if (w + wordData.width > this.lineWidth) {
                        // new line
                        if (w > maxW) { maxW = w; }
                        w = 0;
                        wordsDataForLine = [];
                        lineData = { wordsData: wordsDataForLine, width: 0 };
                        lines.push(lineData);
                    }
                    // At start of line, dont add wordData if it is just a space
                    if (wordsDataForLine.length > 0 || wordData.text != " ") {
                        w += wordData.width;
                        wordsDataForLine.push(wordData);
                        lineData.width = w;
                    }
                }
            } else {
                wordsDataForLine = wordsDataForHardLine;
                var w = 0;
                wordsDataForHardLine.forEach(function (wordData) {
                    w += wordData.width;
                });
                lines.push({ wordsData: wordsDataForLine, width: w });
            }
        }

        // Performance improvement for drawing: per line, cluster consecutive words with same font again
        for (var i = 0; i < lines.length; i++) {
            var wordsDataForLine = lines[i].wordsData;
            for (var j = 0; j < wordsDataForLine.length - 1; j++) {
                var w0 = wordsDataForLine[j];
                var w1 = wordsDataForLine[j + 1];
                if (w0.tag === w1.tag) {
                    w0.text += w1.text;
                    w0.width += w1.width;
                    wordsDataForLine.splice(j + 1, 1);
                    j--;
                }
            }
        }

        //Store total text measurements
        this._metrics = {};
        var lineHeight = this.lineHeight;
        this._metrics.height = lines.length * lineHeight;
        this._metrics.width = maxW;

        this.linesData = lines;
    }
}

var REGEX_TOKENS = /(<.*?>(?:.|\n|\r|\r\n)*?<\/.*?>)/g;//we dont care about matching closing tag, as long as there is a closing tag
var REGEX_TAGS = /(?:<(.*?)>)((?:.|\r|\n|\r\n)*?)(?:<\/.*?>)/g;//1st capture: complete string, 2nd capture: tagname, 3rd: capture: text between tags
var REGEX_LINEBREAKS = /\r\n|\r|\n/g;//match linebreaks